import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./pubblicita.module.css";
import { getSponsorImageUrl } from "@logic/sponsor";
import { ROUTES, useNavigator } from "@contexts/NavigatorContext";

const SPONSOR_TYPES = {
  BANNER: 1,
  SEAMLESS: 2,
};

const SPONSOR_DESTINATIONS = {
  LINK: 1,
  IMAGE: 2,
};

const SponsorPreview = ({ sponsor, slot }) => {
  const { navigate } = useNavigator();
  const isSeamless = sponsor?.sponsorType === SPONSOR_TYPES.SEAMLESS;
  const imageNames = sponsor?.images || [];

  const squareImage = useMemo(() => (
    imageNames.includes("square")
      ? getSponsorImageUrl({ userId: sponsor.userId, sponsorId: sponsor.id, imageName: "square" })
      : null
  ), [imageNames, sponsor.id, sponsor.userId]);

  const bannerImage = useMemo(() => (
    imageNames.includes("small")
      ? getSponsorImageUrl({ userId: sponsor.userId, sponsorId: sponsor.id, imageName: "small" })
      : null
  ), [imageNames, sponsor.id, sponsor.userId]);

  const bannerWideImage = useMemo(() => (
    imageNames.includes("big")
      ? getSponsorImageUrl({ userId: sponsor.userId, sponsorId: sponsor.id, imageName: "big" })
      : null
  ), [imageNames, sponsor.id, sponsor.userId]);

  const destinationImage = useMemo(() => (
    imageNames.includes("destination")
      ? getSponsorImageUrl({ userId: sponsor.userId, sponsorId: sponsor.id, imageName: "destination" })
      : null
  ), [imageNames, sponsor.id, sponsor.userId]);

  const onClick = useCallback(() => {
    if (!sponsor) {
      return;
    }

    if (sponsor.sponsorDestinationType === SPONSOR_DESTINATIONS.LINK && sponsor.destinationUrl) {
      window.open(sponsor.destinationUrl, "_blank", "noopener");
      return;
    }

    if (sponsor.sponsorDestinationType === SPONSOR_DESTINATIONS.IMAGE && destinationImage) {
      navigate(ROUTES.SPONSOR_DESTINAZIONE, [sponsor.id, sponsor.userId]);
    }
  }, [destinationImage, navigate, sponsor]);

  const onKeyDown = useCallback((event) => {
    if (event.key === "Enter" || event.key === " ") {
      onClick();
    }
  }, [onClick]);

  if (!sponsor) {
    return null;
  }

  if (!squareImage && !bannerImage && !bannerWideImage) {
    return (
      <div className={styles.pubblicitaPlaceholder}>
        <span>Failed to load sponsor</span>
      </div>
    );
  }

  return (
    <>
      {isSeamless ? (
      <div
        className={styles.pubblicita}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
        aria-label={sponsor.title || "Sponsor"}
        data-slot={slot}
      >
        <div className={styles.seamless}>
          {squareImage && <img src={squareImage} alt={sponsor.title || "Sponsor"} />}
        </div>
        <div className={styles.mainInfo}>
          <span className={styles.title}>{sponsor.title || "Sponsor"}</span>
          <span className={styles.description}>{sponsor.description}</span>
          <span className={styles.extra}>Sponsorizzato</span>
        </div>
      </div>
      ) : (
        <div className={styles.banner} onClick={onClick} onKeyDown={onKeyDown}>
          {bannerImage && (
            <img src={bannerImage} alt={sponsor.title || "Sponsor"} className={styles.bannerSmall} />
          )}
        </div>
      )}</>
  );
};

SponsorPreview.propTypes = {
  sponsor: PropTypes.shape({
    id: PropTypes.number,
    userId: PropTypes.number,
    sponsorType: PropTypes.number,
    sponsorDestinationType: PropTypes.number,
    destinationUrl: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
  }),
  slot: PropTypes.number,
};

SponsorPreview.defaultProps = {
  sponsor: null,
  slot: null,
};

export default SponsorPreview;
